import SpeakButton from "../EnglishTool/SpeakButton";
import { getReadingPool } from "../EnglishTool/pool/readingPool";
import { getSentencePool } from "../EnglishTool/pool/sentencePool";

export const chapterPages = {
    1: [
      {
        title: <h2>Meta-Language: Noun Clauses & Reflexive Critique</h2>,
        body: (
          <>
            <p>
              <b>Noun Clauses as Interpretive Frameworks:</b> Advanced essays often use noun clauses to frame epistemological questions or challenge literary assumptions.<br />
              <b>Subject Example:</b> <i><b>That all narratives are constructed</b> is central to poststructuralist theory.</i>
              <SpeakButton text="That all narratives are constructed is central to poststructuralist theory." /><br />
              <b>Object Example:</b> <i>We must consider <b>whether meaning is fixed or contingent</b> within the text.</i>
              <SpeakButton text="We must consider whether meaning is fixed or contingent within the text." />
            </p>
            <p>
              <b>Nominalization & Meta-Critique:</b> Use nominalization to question the act of criticism itself.<br />
              <i>"Scholars debate endlessly." → "The <b>debate</b> about textual indeterminacy is ongoing."</i>
              <SpeakButton text="The debate about textual indeterminacy is ongoing." />
              <br />
              <b>Challenge:</b> Reframe "The author reflects on identity." using nominalization and a critical lens.
            </p>
            <p>
              <b>Appositives in Meta-Commentary:</b> Use embedded appositives to situate your critique within broader intellectual debates.<br />
              <i>Her assertion, <b>a direct engagement with Derrida’s différance</b>, redefines narrative closure.</i>
              <SpeakButton text="Her assertion, a direct engagement with Derrida’s différance, redefines narrative closure." />
              <br />
              <b>Try it:</b> Identify the appositive and its critical role: "This chapter, <b>a meditation on loss</b>, questions closure."
            </p>
            <p>
              <b>Meta-Reading:</b>
              <br />
              <i>{getReadingPool(12)[0]?.statement}</i>
              <br />
              <b>Task:</b> How does grammatical structure contribute to self-reflexivity or meta-analysis in the passage?
            </p>
          </>
        ),
      },
      {
        title: <h2>Theme, Motif & Intertextuality in Postmodern Literature</h2>,
        body: (
          <>
            <p>
              <b>Theme as Meta-Narrative:</b> Explore how texts question the nature of theme itself, or blur the line between author and reader.<br />
              <b>Motif as Palimpsest:</b> Identify motifs (mirrors, doubles, labyrinths) that reappear across genres as both homage and critique.<br />
              <b>Example:</b> <i>The labyrinth motif in Borges, Eco, and Calvino represents both the search for meaning and the impossibility of final answers.</i>
              <SpeakButton text="The labyrinth motif in Borges, Eco, and Calvino represents both the search for meaning and the impossibility of final answers." />
            </p>
            <p>
              <b>Intertextuality:</b> Examine how contemporary works explicitly reference, parody, or subvert earlier texts.<br />
              <i>
                "By echoing Hamlet’s soliloquy, Stoppard’s play interrogates agency and authorship."
              </i>
              <SpeakButton text="By echoing Hamlet’s soliloquy, Stoppard’s play interrogates agency and authorship." />
            </p>
            <p>
              <b>Challenge:</b> Trace an intertextual motif through at least two literary or media works; explain its evolution and critical significance.
            </p>
            <p>
              <b>Meta-Critical Task:</b> Write a paragraph that interrogates the act of interpretation itself.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Synthesis, Meta-Analysis & Creative Argument</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Deconstruct a sentence for noun clauses, appositives, and nominalizations, explaining how each shapes meaning.<br />
              "That language is inherently unstable, a premise championed by deconstructionists, calls into question all claims to truth."
              <SpeakButton text="That language is inherently unstable, a premise championed by deconstructionists, calls into question all claims to truth." />
            </p>
            <p>
              <b>Task 2:</b> Compose an introduction for a research paper that problematizes interpretation, using at least one noun clause and a meta-critical appositive.
            </p>
            <p>
              <b>Challenge:</b> Write a thesis that both advances an argument and questions the grounds of argument itself.
            </p>
          </>
        ),
      },
    ],
  
    2: [
      {
        title: <h2>Voice, Modality & Critical Distance in Advanced Critique</h2>,
        body: (
          <>
            <p>
              <b>Voice as Stance:</b> Use both active and passive strategically to foreground methodology or to distance yourself from claims.<br />
              <i>"It has been argued that all texts are intertextual." (passive for theoretical distance)</i>
              <SpeakButton text="It has been argued that all texts are intertextual." />
              <i>"Kristeva reframes authorship as collaboration." (active for attribution)</i>
              <SpeakButton text="Kristeva reframes authorship as collaboration." />
            </p>
            <p>
              <b>Modal Verbs for Meta-Analysis:</b> Use "may," "could," "might" to suggest theoretical possibilities, question certainty, or hedge against over-claim.<br />
              <i>"The poem might be read as both confession and performance."</i>
              <SpeakButton text="The poem might be read as both confession and performance." />
              <b>Try it:</b> Restate "Critics prove the novel’s postmodernism" using modality and critical distance.
            </p>
            <p>
              <b>Subjunctive & Conditional:</b> Analyze how hypothetical reasoning allows for competing interpretations.<br />
              <i>"If meaning were stable, the text would resist deconstruction."</i>
              <SpeakButton text="If meaning were stable, the text would resist deconstruction." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Meta-Syntax: Complex & Fragmented Literary Style</h2>,
        body: (
          <>
            <p>
              <b>Layered, Fragmented, and Experimental Sentences:</b> Use, analyze, and critique non-standard syntax for effect.<br />
              <i>
                "Fragmented memories. Ellipses. The absence of closure—these constitute the text’s style."
              </i>
              <SpeakButton text="Fragmented memories. Ellipses. The absence of closure—these constitute the text’s style." />
            </p>
            <p>
              <b>Clarity, Obscurity, and Literary Effect:</b> Identify when run-ons or fragments serve a purpose (e.g., stream of consciousness, trauma narrative) rather than error.<br />
              <i>Example of purposeful fragment: "Because nothing is certain anymore."</i>
              <SpeakButton text="Because nothing is certain anymore." />
            </p>
            <p>
              <b>Challenge:</b> Write a passage that mixes standard and experimental syntax to reflect uncertainty or fragmentation.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Argumentation & Meta-Critical Reflection</h2>,
        body: (
          <>
            <p>
              <b>Task 1:</b> Identify verbs (including modals and passives) in a passage and explain how they construct critical stance or uncertainty.<br />
              <i>{getSentencePool(12).find(s => s.focus === "verbs")?.sentence}</i>
            </p>
            <p>
              <b>Task 2:</b> Compose a critical claim about ambiguity in a literary work, using meta-language, modality, and subordinate structure.
            </p>
            <p>
              <b>Challenge:</b> Rewrite a standard summary as a meta-critical reflection.
            </p>
          </>
        ),
      },
    ],
  
    3: [
      {
        title: <h2>Advanced Adjectives: Meta-Theory, Critique & Beyond</h2>,
        body: (
          <>
            <p>
              <b>Meta-Theoretical Adjectives:</b> Use adjectives that reflect schools of thought: "deconstructive," "performative," "intertextual," "problematizing."<br />
              <i>"The <b>deconstructive</b> reading destabilizes closure."</i>
              <SpeakButton text="The deconstructive reading destabilizes closure." />
            </p>
            <p>
              <b>Evaluative Adjectives in Meta-Analysis:</b> Push evaluative terms beyond "insightful" to "transgressive," "self-reflexive," "ironic," "polyphonic."<br />
              <i>"This is a <b>self-reflexive</b> and <b>ironic</b> commentary on authorship."</i>
              <SpeakButton text="This is a self-reflexive and ironic commentary on authorship." />
            </p>
            <p>
              <b>Challenge:</b> Use at least two meta-theoretical adjectives in a sentence critiquing literary tradition or genre.
            </p>
          </>
        ),
      },
      {
        title: <h2>Comparative, Superlative & Meta-Critical Synthesis</h2>,
        body: (
          <>
            <p>
              <b>Comparative Evaluation:</b> Go beyond content: compare *methods*, *interpretations*, and *frameworks*.<br />
              <i>"More disruptive than modernist ambiguity, postmodern indeterminacy is fundamentally meta-textual."</i>
              <SpeakButton text="More disruptive than modernist ambiguity, postmodern indeterminacy is fundamentally meta-textual." />
            </p>
            <p>
              <b>Superlative Critique:</b> Justify why a method is "most generative," "least constrained," etc., with meta-argument.<br />
              <i>"Of the analytic models applied, intertextuality proves the most generative."</i>
              <SpeakButton text="Of the analytic models applied, intertextuality proves the most generative." />
            </p>
            <p>
              <b>Challenge:</b> Synthesize at least two approaches, using meta-theoretical language and advanced comparative/superlative adjectives.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Meta-Analysis & Reflective Critique</h2>,
        body: (
          <>
            <p>
              <b>Task:</b> Identify and explain meta-theoretical and evaluative adjectives in a provided passage; explain their function in critical discourse.
            </p>
            <p>
              <b>Compose:</b> Write a paragraph comparing critical approaches to a text or theme, using meta-theoretical and evaluative adjectives.
            </p>
          </>
        ),
      },
    ],
  
    4: [
      {
        title: <h2>Adverbs, Transitions & Meta-Discourse in Postmodern Critique</h2>,
        body: (
          <>
            <p>
              <b>Meta-Adverbs:</b> Employ adverbs and discourse markers that reflect epistemological uncertainty or critique: "problematically," "reflexively," "ostensibly," "self-consciously."<br />
              <i>"Reflexively, the essay foregrounds its own construction."</i>
              <SpeakButton text="Reflexively, the essay foregrounds its own construction." />
            </p>
            <p>
              <b>Transitions for Meta-Argument:</b> Use transitions to both connect ideas and question them: "insofar as," "supposedly," "by extension," "ironically."<br />
              <i>"By extension, this approach undermines any singular interpretation."</i>
              <SpeakButton text="By extension, this approach undermines any singular interpretation." />
            </p>
            <p>
              <b>Media & Discourse Analysis:</b> Examine how adverbs, transitions, and meta-language are used in contemporary essays, editorials, and theory.<br />
              <i>"Problematically, the article collapses author and narrator."</i>
              <SpeakButton text="Problematically, the article collapses author and narrator." />
            </p>
          </>
        ),
      },
      {
        title: <h2>Critical Adverbial Mastery & Meta-Discourse Analysis</h2>,
        body: (
          <>
            <p>
              <b>Find and Explain:</b> Locate meta-adverbs and discourse markers in a provided passage, and analyze how they signal self-reflexivity, skepticism, or ambiguity.
            </p>
            <p>
              <b>Compose:</b> Write a meta-critical argument about interpretation, using at least three meta-adverbs, two meta-transitions, and one theoretical concept.
            </p>
          </>
        ),
      },
      {
        title: <h2>Practice: Reflective Synthesis & Meta-Argument</h2>,
        body: (
          <>
            <p>
              <b>Compose:</b> An extended, reflective paragraph that analyzes the process of critical reading itself, using meta-language, theoretical terms, and comparative logic.
            </p>
            <p>
              <b>Challenge:</b> How does your own writing foreground (or conceal) its own assumptions, methods, or interpretive strategies?
            </p>
          </>
        ),
      },
    ]
  };